/**
 * 照护档案背面「你写下的叮嘱」（RECEPTION-CORRECT-01，6c2b 2026-10-09；关 CLOSE-MEM 最后一个缺口，2531 MEM-M0-CLOSE-01 的 G1）：
 * 主人自己确认过的生活叮嘱，每条都能「改一下」「不用了」（只留在这里的那几条叫「不留了」，见下面 stopWordsOf）。
 *
 * - 只给写下它的那个人：列表读 GET /pets/{pet_id}/care-notes，后端只取登录这个人自己的（reception/store.py 的 _notes 按 user_id 取；
 *   路由说明“接待与叮嘱是每位家人自己的，别的家人看不到”），更正接口对别人的叮嘱也只回“没有找到”。契约里的 CareNote 没有作者字段，
 *   前端没有“这条是不是我写的”可判——所以这两个入口只挂在这份按本人取回的列表上；卡面上的“习惯 / 怕 / 爱吃”（档案里共用的部分）
 *   和别处（家园欢迎、TA 的档案里的出处）一律不挂。查询键带 userId，换号不串缓存。
 * - 改一下：就地改，保存走 POST /care-notes/{id}/corrections（action=correct；后端写新版本，同一事务里撤掉旧版本的授权）。
 * - 不用了：先轻确认一次，再走同一个接口（action=revoke）。说法按去向分（RECEPTION-CORRECT-01b，I 10-09 看截图定）：交给 TA 的说「TA 不会再按这条来做」；
 *   只留在这里的本来就不交给 TA，动作叫「不留了」、确认说「这条就不再记着了。」，不提 TA。
 * - 一次动作一个幂等键：失败后原样再点沿用同一个键（后端同键同内容只记一次），改了内容才换新键。
 *   结果不明（断网、超时、上一次还在处理）不自动重发，写的字留着，由主人自己再点。
 * - 成功后先按回包改缓存（旧版本记成已撤回、新版本接在原位），再失效重读；照护档案卡面和家园欢迎由同一批叮嘱投影出来，一起重读。
 * - 撤回以后照列表回包显示：列表里还留着（带撤回时间）就标“已停用”，不再给就不显示；抹除过的（文字已清空）不显示。
 *   停用以后那句明白话紧贴在被停用的那一条下面（「已停用」后面，卡面次要墨色，读屏念一次）；那一条不在列表里了才退回区块底下（RECEPTION-CORRECT-01b）。
 * - 摸摸偏好（slot=touch_preference）后端只许撤回、不许在这里改：只给「不用了」。
 * - 出错先用 toApiError 的人话（ERROR-RECOVERY-01 落地后改用 shared/api/playerError 的 describeError）。
 * - 动效只标 data-motion="enter"（减少动态时全站只淡入）；按钮用共享 .ps-btn（44px 高），样式见 life.css「你写下的叮嘱」。
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient, type UseQueryResult } from "@tanstack/react-query";
import type { CareNote, MemoryCorrectionRequestInput, MemoryCorrectionResult, SaveTarget } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useCurrentHousehold } from "@/shared/session/householdContext";
import { Button, ErrorState, Icon } from "@/shared/ui";

const FIXTURE_SCOPE = "fixture";

/** 本人叮嘱的查询键：登记在共用的 queryKeys.ownCareNotes（I 10-09 同意，VISIT-LIVE-01 挪进 shared/query）；这里只转一下，不另定义。 */
export const ownCareNotesKey = queryKeys.ownCareNotes;

export type OwnCareNotes = { query: UseQueryResult<CareNote[]>; listKey: readonly unknown[]; enabled?: boolean };

/** 读本人给这一只写过的叮嘱。live 要有登录的人和当前宠物才读；演示模式读演示存档（本标签页里）。 */
export function useOwnCareNotes(petId: string | null | undefined): OwnCareNotes {
  const { reception } = useServices();
  const { userId } = useCurrentHousehold();
  const fixture = env.dataMode === "fixture";
  const pet = petId ?? null;
  const listKey = ownCareNotesKey(fixture ? FIXTURE_SCOPE : userId ?? "-", fixture ? FIXTURE_SCOPE : pet ?? "-");
  const enabled = Boolean(pet?.trim()) && (fixture || Boolean(userId?.trim()));
  const query = useQuery({ queryKey: listKey, queryFn: () => {
    if (!enabled) throw new Error("Inactive care-note context");
    return reception.notes(pet ?? "");
  }, enabled });
  return { query, listKey, enabled };
}

export type ShownNote = { note: CareNote; state: "active" | "stopped" };

/**
 * 按列表回包排出要显示的：改过的只显示最新一版，放在最早那一版的位置上（就地改完不跳到最后）；
 * 撤回了、又没有新版本接着的，就是“不用了”的那条，标已停用；抹除过的（文字已清空）不显示。
 */
export function shownNotes(notes: CareNote[]): ShownNote[] {
  const byId = new Map(notes.map((note) => [note.note_id, note]));
  const next = new Map<string, CareNote>();
  for (const note of notes) if (note.supersedes_note_id && byId.has(note.supersedes_note_id)) next.set(note.supersedes_note_id, note);
  const out: ShownNote[] = [];
  const seen = new Set<string>();
  for (const note of notes) {
    if (note.supersedes_note_id && byId.has(note.supersedes_note_id)) continue; // 由最早那一版带出来
    let latest = note;
    const chain = new Set([latest.note_id]);
    for (let after = next.get(latest.note_id); after && !chain.has(after.note_id); after = next.get(latest.note_id)) {
      latest = after;
      chain.add(after.note_id);
    }
    if (seen.has(latest.note_id) || !latest.text.trim()) continue;
    seen.add(latest.note_id);
    out.push({ note: latest, state: latest.revoked_at ? "stopped" : "active" });
  }
  return out;
}

/**
 * 更正成功后：先按回包改缓存（旧版本记上撤回时间——只用来马上换成“已停用”或被新版本接上，以重读回来的为准；新版本接在后面，
 * shownNotes 会把它放回原位），再失效重读本人的叮嘱、照护档案卡面（卡面上的“叮嘱：”几行由同一批叮嘱投影出来）和家园（欢迎细节同理）。
 */
export function applyCorrection(queryClient: QueryClient, listKey: readonly unknown[], result: MemoryCorrectionResult) {
  const at = new Date().toISOString();
  queryClient.setQueryData<CareNote[]>(listKey, (prev) => {
    if (!prev) return prev;
    const marked = prev.map((note) => (note.note_id === result.note_id && !note.revoked_at ? { ...note, revoked_at: at } : note));
    const fresh = result.new_note;
    return fresh && !marked.some((note) => note.note_id === fresh.note_id) ? [...marked, fresh] : marked;
  });
  void queryClient.invalidateQueries({ queryKey: listKey });
  void queryClient.invalidateQueries({ queryKey: ["life", "credential"] });
  void queryClient.invalidateQueries({ queryKey: queryKeys.home });
}

type Action = "correct" | "revoke";

/** 出错怎么说（人话，不带错误码）。changed：这条已经在别处改过、停过或不在了，要重读列表。confirm：这一条的确认按钮叫什么（按去向分，见 stopWordsOf）。 */
function failureOf(error: unknown, action: Action, confirm: string): { changed: boolean; text: string } {
  const err = toApiError(error);
  const reason = typeof err.details?.reason === "string" ? err.details.reason : null;
  // 结果不明：断网、超时、同一个键的上一次还在处理。不自动重发；主人再点沿用同一个键，后端只记一次。
  if ((err.kind === "network" && !err.fromException) || err.kind === "timeout" || err.code === "IDEMPOTENCY_IN_PROGRESS") {
    return {
      changed: false,
      text: action === "correct" ? "没能确认改好没有。你写的还在，可以再点一次「保存」，不会记成两条。" : `没能确认停下没有。可以再点一次「${confirm}」，不会重复停。`,
    };
  }
  if (err.isNotFound || err.code === "VERSION_CONFLICT" || reason === "revoked") return { changed: true, text: "这条刚在别处改过，已经换成最新的了。" };
  return { changed: false, text: action === "correct" ? `没有改成：${err.playerMessage} 你写的还在，可以再试一次。` : `没能停用：${err.playerMessage}` };
}

const TARGET_TAG: Partial<Record<SaveTarget, string>> = { give_to_pet: "交给 TA", keep_here: "只留在这里" };

/**
 * 停用这一条怎么说（RECEPTION-CORRECT-01b，I 10-09 看截图定）：按去向分两套。
 * - 交给 TA（give_to_pet；摸摸偏好也是这个去向）：TA 会按它来做，所以说「不用了」「TA 不会再按这条来做」；
 * - 别的去向都不交给 TA，不提 TA：只留在这里（keep_here）本来就只是主人自己的记录，说「不留了」「这条就不再记着了」；
 *   契约里还有不保存（do_not_save），后端确认时直接跳过、不会存成叮嘱（reception/store.py），万一出现也按这一套说——不替 TA 说会怎样。
 */
type StopWords = { action: string; group: string; ask: string; confirm: string; done: string };
const STOP_FOR_PET: StopWords = { action: "不用了", group: "确认不用这条", ask: "不用以后，TA 不会再按这条来做。", confirm: "确定不用了", done: "这条不用了，TA 不会再按它来做。" };
const STOP_FOR_HERE: StopWords = { action: "不留了", group: "确认不留这条", ask: "这条就不再记着了。", confirm: "确定不留了", done: "这条不再记着了。" };
function stopWordsOf(target: SaveTarget): StopWords {
  return target === "give_to_pet" ? STOP_FOR_PET : STOP_FOR_HERE;
}

/** 区块里说的一句话。noteId 有值的是停用以后的明白话，紧贴在被停用的那一条下面；没有的（改好了、别处改过）照旧在区块底下。 */
/** ok：改好了（成功）那一句，前面带对勾（UI-BASE-01b，W-09）；「别处改过」这类不是成功，不带。 */
type Said = { text: string; noteId: string | null; ok?: boolean };

function NoteRow({ note, listKey, onSaid }: { note: CareNote; listKey: readonly unknown[]; onSaid: (said: Said) => void }) {
  const { reception } = useServices();
  const queryClient = useQueryClient();
  const textId = useId();
  const editId = useId();
  const [mode, setMode] = useState<"view" | "edit" | "stop">("view");
  const [draft, setDraft] = useState(note.text);
  const [failure, setFailure] = useState<string | null>(null);
  const sending = useRef(false);
  const lastKey = useRef<{ sig: string; key: string } | null>(null);
  const editor = useRef<HTMLTextAreaElement>(null);
  const mounted = useRef(false);
  const owner = useRef(Symbol("care-note-owner"));
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const current = (token: symbol) => mounted.current && owner.current === token;

  useEffect(() => {
    if (mode === "edit" && current(owner.current)) editor.current?.focus();
  }, [mode]);
  const words = stopWordsOf(note.target);

  const correct = useMutation({
    mutationFn: async ({ body, token }: { body: MemoryCorrectionRequestInput; token: symbol }) => {
      if (!current(token)) throw new Error("Inactive care-note context");
      // 同一次动作（同样的内容）沿用同一个键；内容变了才换新键（后端同键不同内容会拒）
      const sig = JSON.stringify(body);
      const key = lastKey.current?.sig === sig ? lastKey.current.key : newIdempotencyKey("care-note-correct");
      lastKey.current = { sig, key };
      try {
        const result = await reception.correct(note.note_id, body, key);
        if (!current(token)) throw new Error("Inactive care-note context");
        return result;
      } catch (error) {
        // 旧鉴权错误在全局 MutationCache 之前截断，不刷新新身份的会话。
        if (!current(token)) throw new Error("Inactive care-note context");
        throw error;
      }
    },
    retry: false, // 结果不明也不自动重发
    onSuccess: (result, { body, token }) => {
      if (!current(token)) return;
      lastKey.current = null;
      applyCorrection(queryClient, listKey, result);
      onSaid(body.action === "correct" ? { text: "改好了，以后按新的来。", noteId: null, ok: true } : { text: words.done, noteId: note.note_id });
    },
    onError: (error, { body, token }) => {
      if (!current(token)) return;
      const failed = failureOf(error, body.action === "correct" ? "correct" : "revoke", words.confirm);
      if (failed.changed) {
        void queryClient.invalidateQueries({ queryKey: listKey });
        onSaid({ text: failed.text, noteId: null });
      }
      setFailure(failed.text);
    },
    onSettled: (_result, _error, { token }) => {
      if (!current(token)) return;
      sending.current = false;
    },
  });
  const pending = correct.isPending;
  const send = (body: MemoryCorrectionRequestInput) => {
    if (!current(owner.current) || sending.current) return; // 连点：上一次还没回来就不再发
    sending.current = true;
    setFailure(null);
    correct.mutate({ body, token: owner.current });
  };
  const trimmed = draft.trim();
  const canSave = trimmed.length > 0 && trimmed !== note.text.trim();
  const editable = note.slot !== "touch_preference";
  const tag = TARGET_TAG[note.target];

  return (
    <li className="ps-careback__own-item" data-testid="own-note" data-state="active">
      {mode === "edit" ? (
        <div className="ps-careback__own-edit" data-motion="enter">
          <label className="ps-careback__own-label" htmlFor={editId}>
            改成这样
          </label>
          <textarea id={editId} ref={editor} className="ps-textarea ps-careback__own-input" value={draft} maxLength={500} readOnly={pending} onChange={(event) => setDraft(event.target.value)} />
          <div className="ps-careback__own-actions">
            <Button variant="primary" loading={pending} disabled={!canSave} onClick={() => send({ action: "correct", expected_version: note.version, new_text: trimmed })}>
              保存
            </Button>
            <Button
              variant="ghost"
              disabled={pending}
              onClick={() => {
                setDraft(note.text);
                setFailure(null);
                setMode("view");
              }}
            >
              算了
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="ps-careback__own-text" id={textId}>
            {note.text}
          </p>
          {tag ? <span className="ps-careback__own-tag">{tag}</span> : null}
          {mode === "stop" ? (
            <div className="ps-careback__own-confirm" role="group" aria-label={words.group} data-motion="enter">
              <p>{words.ask}</p>
              <div className="ps-careback__own-actions">
                <Button variant="danger" loading={pending} aria-describedby={textId} onClick={() => send({ action: "revoke", expected_version: note.version })}>
                  {words.confirm}
                </Button>
                <Button
                  variant="ghost"
                  disabled={pending}
                  onClick={() => {
                    setFailure(null);
                    setMode("view");
                  }}
                >
                  先留着
                </Button>
              </div>
            </div>
          ) : (
            <div className="ps-careback__own-actions">
              {editable ? (
                <Button
                  variant="secondary"
                  aria-describedby={textId}
                  onClick={() => {
                    setDraft(note.text);
                    setFailure(null);
                    setMode("edit");
                  }}
                >
                  改一下
                </Button>
              ) : null}
              <Button
                variant="ghost"
                aria-describedby={textId}
                onClick={() => {
                  setFailure(null);
                  setMode("stop");
                }}
              >
                {words.action}
              </Button>
            </div>
          )}
        </>
      )}
      {failure ? (
        <p className="ps-careback__own-error" role="alert" data-motion="enter">
          <Icon name="alert" size={14} className="ps-careback__state-icon" />
          {failure}
        </p>
      ) : null}
    </li>
  );
}

/**
 * 停用了的那一条。「已停用」和去向标签（「交给 TA」）用同一个 .ps-careback__own-tag，一样大。
 * 刚停的这一条下面紧贴一句明白话（RECEPTION-CORRECT-01b）：卡面次要墨色、不用强调色；role=status 跟着这一条，读屏念一次。
 */
function StoppedRow({ note, said }: { note: CareNote; said: string | null }) {
  return (
    <li className="ps-careback__own-item is-stopped" data-testid="own-note" data-state="stopped">
      <p className="ps-careback__own-text">{note.text}</p>
      <span className="ps-careback__own-tag">已停用</span>
      {said ? (
        <p className="ps-careback__own-stopnote" role="status" data-motion="enter">
          {said}
        </p>
      ) : null}
    </li>
  );
}

/** 照护档案背面「你写下的叮嘱」：读到了才出现；读不到时说一句、给重试，卡面上原来那几行照旧列着（见 faces.tsx 的 CareBack）。 */
export function OwnCareNotesSection({ own }: { own: OwnCareNotes }) {
  const { reception } = useServices();
  const [serviceOwner, setServiceOwner] = useState({ reception, epoch: 0 });
  if (serviceOwner.reception !== reception) setServiceOwner({ reception, epoch: serviceOwner.epoch + 1 });
  if (own.enabled === false) return null;
  // 同 note_id 也只属于本次列表/服务：草稿、确认、成功提示与原键全部随范围卸载。
  return <OwnCareNotesFor key={JSON.stringify([env.dataMode, own.listKey, serviceOwner.epoch])} own={own} />;
}

function OwnCareNotesFor({ own }: { own: OwnCareNotes }) {
  const titleId = useId();
  const [said, setSaid] = useState<Said | null>(null);
  const { query, listKey } = own;
  if (query.isError) {
    return (
      <section className="ps-careback__own" aria-labelledby={titleId} data-testid="own-care-notes">
        <h3 id={titleId}>你写下的叮嘱</h3>
        <p className="ps-careback__own-lead">叮嘱这会儿改不了，上面列的照常记着。</p>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      </section>
    );
  }
  const shown = query.data ? shownNotes(query.data) : [];
  if (!shown.length) return null;
  // 停用以后的明白话跟着被停用的那一条（RECEPTION-CORRECT-01b）：那一条已标停用，就放在它下面。回包先到、列表下一拍才换成停用时先不说——
  // 免得先在区块底下冒一句、再挪进那一条，读屏念两遍（起草时用例 1b-1 实测过会这样）；那一条不在列表里了（后端不再给）才退回区块底下。
  const stoppedId = said?.noteId ?? null;
  const anchor = stoppedId === null ? undefined : shown.find(({ note }) => note.note_id === stoppedId);
  const pinned = anchor?.state === "stopped";
  const atBottom = stoppedId === null || anchor === undefined;
  return (
    <section className="ps-careback__own" aria-labelledby={titleId} data-testid="own-care-notes">
      <h3 id={titleId}>你写下的叮嘱</h3>
      <ul className="ps-careback__own-list">
        {shown.map(({ note, state }) =>
          state === "active" ? (
            <NoteRow key={note.note_id} note={note} listKey={listKey} onSaid={setSaid} />
          ) : (
            <StoppedRow key={note.note_id} note={note} said={pinned && said && note.note_id === stoppedId ? said.text : null} />
          ),
        )}
      </ul>
      {said && atBottom ? (
        <p className={said.noteId ? "ps-careback__own-stopnote" : "ps-careback__own-said"} role="status" data-motion="enter">
          {said.ok ? <Icon name="check" size={14} className="ps-careback__state-icon" /> : null}
          {said.text}
        </p>
      ) : null}
    </section>
  );
}
